import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Fade,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Lightbulb as SuggestionIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  ChatBubble as ChatIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const ChatBot = ({ isOpen, onToggle, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(Date.now().toString());
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  // Messages de bienvenue
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 1,
          text: "👋 Bonjour ! Je suis votre assistant IA SaasArtisans. Comment puis-je vous aider aujourd'hui ?",
          sender: 'bot',
          timestamp: new Date(),
          suggestions: [
            "Trouver un plombier",
            "Estimer le coût d'une rénovation",
            "Voir les artisans disponibles",
            "Questions sur les services"
          ]
        }
      ]);
    }
  }, [isOpen, messages.length]);

  // Auto-scroll vers le bas
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Envoyer un message
  const handleSendMessage = async (text = null) => {
    const messageText = text || inputMessage.trim();
    if (!messageText || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageText,
          sessionId: sessionId,
          context: {
            timestamp: new Date().toISOString(),
            platform: 'web'
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        const botMessage = {
          id: Date.now() + 1,
          text: data.data.response,
          sender: 'bot',
          timestamp: new Date(),
          confidence: data.data.confidence,
          suggestions: data.data.suggestions || [],
          metadata: data.data.metadata
        };

        setMessages(prev => [...prev, botMessage]);
        setSuggestions(data.data.suggestions || []);
      } else {
        throw new Error(data.message || 'Erreur de communication');
      }
    } catch (error) {
      console.error('Erreur chatbot:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "😅 Désolé, je rencontre un petit problème technique. Pouvez-vous réessayer ?",
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Gestion des touches clavier
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Suggestions rapides
  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
    setSuggestions([]);
  };

  // Interface du chatbot flottant (minimisé)
  if (!isOpen) {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={onToggle}
          sx={{
            borderRadius: '50%',
            width: 60,
            height: 60,
            minWidth: 60,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 6px 25px rgba(33, 150, 243, 0.4)'
            }
          }}
        >
          <ChatIcon sx={{ fontSize: 30 }} />
        </Button>
      </motion.div>
    );
  }

  // Interface du chatbot ouvert
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 20,
          right: 20,
          top: 'auto',
          left: 'auto',
          margin: 0,
          width: 400,
          maxWidth: '90vw',
          height: 600,
          maxHeight: '80vh',
          borderRadius: 3,
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
        }
      }}
    >
      {/* En-tête du chatbot */}
      <Box
        sx={{
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <BotIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">Assistant IA</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {isLoading ? 'En train d\'écrire...' : 'En ligne'}
            </Typography>
          </Box>
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{ color: 'white', mr: 1 }}
          >
            <MinimizeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Zone des messages */}
      <Box
        sx={{
          flex: 1,
          p: 1,
          overflowY: 'auto',
          maxHeight: 400,
          bgcolor: '#f5f5f5'
        }}
      >
        <List sx={{ p: 0 }}>
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start',
                    py: 1,
                    px: 2
                  }}
                >
                  <Card
                    sx={{
                      maxWidth: '80%',
                      bgcolor: message.sender === 'user' 
                        ? 'primary.main' 
                        : message.isError 
                          ? 'error.light' 
                          : 'white',
                      color: message.sender === 'user' ? 'white' : 'text.primary',
                      boxShadow: 1,
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        {message.sender === 'bot' && (
                          <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                            <BotIcon sx={{ fontSize: 14 }} />
                          </Avatar>
                        )}
                        <Box flex={1}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {message.text}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              opacity: 0.7, 
                              mt: 0.5,
                              display: 'block'
                            }}
                          >
                            {message.timestamp.toLocaleTimeString('fr-FR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                            {message.confidence && (
                              <Chip 
                                label={`${Math.round(message.confidence * 100)}% sûr`}
                                size="small"
                                sx={{ ml: 1, height: 16, fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </ListItem>

                {/* Suggestions pour le message bot */}
                {message.sender === 'bot' && message.suggestions && message.suggestions.length > 0 && (
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', pt: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <SuggestionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Suggestions rapides:
                      </Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {message.suggestions.map((suggestion, index) => (
                        <Chip
                          key={index}
                          label={suggestion}
                          variant="outlined"
                          size="small"
                          onClick={() => handleSuggestionClick(suggestion)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </ListItem>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Indicateur de frappe */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <ListItem sx={{ justifyContent: 'flex-start' }}>
                <Card sx={{ bgcolor: 'white', boxShadow: 1, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                      <BotIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      L'assistant réfléchit...
                    </Typography>
                  </CardContent>
                </Card>
              </ListItem>
            </motion.div>
          )}
        </List>
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Zone de saisie */}
      <Box sx={{ p: 2, bgcolor: 'white' }}>
        <Box display="flex" gap={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Tapez votre message..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            sx={{
              bgcolor: inputMessage.trim() ? 'primary.main' : 'grey.300',
              color: 'white',
              '&:hover': {
                bgcolor: inputMessage.trim() ? 'primary.dark' : 'grey.400'
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500'
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>

        {/* Suggestions globales */}
        {suggestions.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Suggestions:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {suggestions.map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion}
                  variant="outlined"
                  size="small"
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ChatBot;